/**
 * GAME 8 — PONTS (hashiwokakero). Archipel connecté, ponts sans croisement.
 * Issu du prototype (index.html:1579-1821), durci en phase difficulté :
 * porte de qualité anti « 1 pont partout » (leurres, croisements, îles forcées,
 * Σ ponts minimum), densité du Grand large par niveau, boucle 128 essais et
 * repli au meilleur score de propagation pour N ≥ 13, `par` = Σ solW.
 */
import type { GameEngine, PuzzleBase, Rng } from '@ordre-cache/shared';
import { ri, shuffle } from '@ordre-cache/shared';
import { D4, mk } from './util';

export interface PontsIsland {
  r: number;
  c: number;
}

export interface PontsEdge {
  a: number;
  b: number;
  dir: number;
  cells: number[];
}

export interface PontsPuzzle extends PuzzleBase {
  g: 'ponts';
  noDouble: boolean;
  isl: PontsIsland[];
  edges: PontsEdge[];
  cross: number[][];
  solW: number[];
  need: number[];
  shown: boolean[];
  w: number[];
  lives: number | null;
  livesMax?: number;
}

interface HaBase {
  isl: PontsIsland[];
  edges: PontsEdge[];
  cross: number[][];
  solW: number[];
  need: number[];
}

let sel: number | null = null;

function haGen(
  N: number,
  islTarget: number,
  noDouble: boolean,
  extraP: number,
  lattice: boolean,
  rng: Rng,
): HaBase | null {
  // 48 essais suffisent aux densités atteignables ; au-delà la géométrie est
  // infaisable et c'est la boucle appelante (relâchement par round) qui ajuste
  for (let tries = 0; tries < 48; tries++) {
    const isl: PontsIsland[] = [];
    const at = mk(N, -1);
    const order: number[] = [];
    if (lattice) {
      // treillis pair/pair : géométrie quasi sans croisements
      for (let r = 0; r < N; r += 2) for (let c = 0; c < N; c += 2) order.push(r * N + c);
      shuffle(rng, order);
    } else {
      for (let i = 0; i < N * N; i++) order.push(i);
      shuffle(rng, order);
    }
    for (const cell of order) {
      if (isl.length >= islTarget) break;
      const r = Math.floor(cell / N),
        c = cell % N;
      let ok = true;
      for (const [dr, dc] of D4) {
        const rr = r + dr,
          cc = c + dc;
        if (rr >= 0 && rr < N && cc >= 0 && cc < N && at[rr][cc] !== -1) {
          ok = false;
          break;
        }
      }
      if (ok) {
        at[r][c] = isl.length;
        isl.push({ r, c });
      }
    }
    if (isl.length < Math.max(4, islTarget * 0.7)) continue;
    const edges: PontsEdge[] = [];
    const seen = new Set<string>();
    for (let i = 0; i < isl.length; i++) {
      const { r, c } = isl[i];
      for (const [dr, dc] of [
        [0, 1],
        [1, 0],
      ]) {
        let rr = r + dr,
          cc = c + dc;
        while (rr < N && cc < N && at[rr][cc] === -1) {
          rr += dr;
          cc += dc;
        }
        if (rr < N && cc < N && at[rr][cc] !== -1 && Math.abs(rr - r) + Math.abs(cc - c) >= 2) {
          const j = at[rr][cc];
          const key = i + '_' + j;
          if (!seen.has(key)) {
            seen.add(key);
            const cells: number[] = [];
            let r2 = r + dr,
              c2 = c + dc;
            while (r2 !== rr || c2 !== cc) {
              cells.push(r2 * N + c2);
              r2 += dr;
              c2 += dc;
            }
            edges.push({ a: i, b: j, dir: dr ? 1 : 0, cells });
          }
        }
      }
    }
    if (edges.length < isl.length - 1) continue;
    const cross: number[][] = edges.map(() => []);
    {
      // carte cellule → arête horizontale, puis balayage des verticales : O(Σ cells)
      const hAt = new Map<number, number>();
      for (let e = 0; e < edges.length; e++)
        if (edges[e].dir === 0) for (const c of edges[e].cells) hAt.set(c, e);
      for (let f = 0; f < edges.length; f++)
        if (edges[f].dir === 1)
          for (const c of edges[f].cells) {
            const e = hAt.get(c);
            if (e !== undefined) {
              cross[e].push(f);
              cross[f].push(e);
            }
          }
    }
    const par = [...Array(isl.length).keys()];
    const find = (x: number): number => {
      while (par[x] !== x) {
        par[x] = par[par[x]];
        x = par[x];
      }
      return x;
    };
    const w = new Array(edges.length).fill(0);
    const occupied = (e: number): boolean => w[e] > 0;
    const eOrder = [...Array(edges.length).keys()];
    shuffle(rng, eOrder);
    for (const e of eOrder) {
      const { a, b } = edges[e];
      if (find(a) === find(b)) continue;
      if (cross[e].some(occupied)) continue;
      w[e] = noDouble ? 1 : 1 + ri(rng, 2);
      par[find(a)] = find(b);
    }
    const roots = new Set<number>();
    for (let i = 0; i < isl.length; i++) roots.add(find(i));
    if (roots.size > 1) continue;
    for (const e of eOrder) {
      if (w[e] || rng() > extraP) continue;
      if (cross[e].some(occupied)) continue;
      w[e] = noDouble ? 1 : 1 + ri(rng, 2);
    }
    const need = new Array(isl.length).fill(0);
    edges.forEach((e, i) => {
      need[e.a] += w[i];
      need[e.b] += w[i];
    });
    if (need.some((n) => n === 0)) continue;
    return { isl, edges, cross, solW: w, need };
  }
  return null;
}

/**
 * Porte de qualité anti « 1 pont partout » (constat Monte-Carlo : 66-82 % des
 * solutions étaient triviales). `f` = relâchement progressif (seuils min ×f,
 * seuils max ÷f) ; la porte est abandonnée au round treillis.
 */
function gateOk(
  base: HaBase,
  shown: boolean[],
  di: number,
  N: number,
  diff: number,
  noDouble: boolean,
  f: number,
): boolean {
  const nI = base.isl.length;
  const degE = new Array(nI).fill(0); // corridors incidents par île
  for (const e of base.edges) {
    degE[e.a]++;
    degE[e.b]++;
  }
  // a. leurres : corridors à 0 pont dans la solution
  const decoys = base.solW.filter((w) => w === 0).length;
  if (decoys < (di === 5 ? [3, 4, 5] : [2, 3, 4])[diff] * f) return false;
  // b. îles « forcées » (need = capacité totale) : déduction gratuite
  const cap0 = noDouble ? 1 : 2;
  let forced = 0,
    shownN = 0;
  for (let i = 0; i < nI; i++) {
    if (!shown[i]) continue;
    shownN++;
    if (base.need[i] === cap0 * degE[i]) forced++;
  }
  if (shownN > 0 && (100 * forced) / shownN > [60, 45, 30][diff] / f) return false;
  // c. sans doubles : « tout à 1 » sans leurres = gagnable en aveugle
  if (noDouble && base.solW.every((w) => w <= 1) && decoys <= 1) return false;
  // d. paires de corridors croisables : matière à conflits
  const crossPairs = base.cross.reduce((s, l) => s + l.length, 0) / 2;
  const minCross = di === 4 ? [4, 8, 12] : di === 3 ? [2, 3, 4] : di === 1 ? null : [1, 2, 4];
  if (minCross && crossPairs < minCross[diff] * f) return false;
  // e. Σ solW minimum (taille de l'ouvrage) — ×0.6 sans doubles (Σ ≈ nb liens, pas ×1,5)
  const sumW = base.solW.reduce((s, v) => s + v, 0);
  let minSum = (N === 7 ? [10, 14, 18] : N === 10 ? [22, 30, 38] : [30, 42, 55])[diff];
  if (noDouble) minSum = Math.ceil(minSum * 0.6);
  if (sumW < minSum * f) return false;
  // f. défis à ponts doubles : assez de liaisons à 2 ponts
  if (!noDouble) {
    const pos = base.solW.filter((w) => w > 0);
    const two = pos.filter((w) => w === 2).length;
    if (pos.length > 0 && (100 * two) / pos.length < [15, 25, 35][diff] * f) return false;
  }
  // g. îles feuilles (1 seul corridor) : liaison évidente
  const leaves = degE.filter((d) => d === 1).length;
  if (nI > 0 && (100 * leaves) / nI > [35, 25, 15][diff] / f) return false;
  // h. Brouillard : une île muette à 1 corridor serait résolue d'office
  if (di === 5) for (let i = 0; i < nI; i++) if (!shown[i] && degE[i] < 2) return false;
  return true;
}

/**
 * Simule la stratégie aveugle « 1 pont sur chaque corridor » (ordre des arêtes,
 * mêmes règles que tap : un corridor croisé par un pont posé reste vide).
 * Retourne true si elle gagne — un tel puzzle est trop lisible.
 */
function blindWins(base: HaBase, shown: boolean[]): boolean {
  const w = new Array(base.edges.length).fill(0);
  for (let e = 0; e < base.edges.length; e++)
    if (!base.cross[e].some((f) => w[f] > 0)) w[e] = 1;
  const deg = new Array(base.isl.length).fill(0);
  base.edges.forEach((e, k) => {
    deg[e.a] += w[k];
    deg[e.b] += w[k];
  });
  for (let i = 0; i < base.isl.length; i++) if (shown[i] && deg[i] !== base.need[i]) return false;
  const par = [...Array(base.isl.length).keys()];
  const find = (x: number): number => {
    while (par[x] !== x) {
      par[x] = par[par[x]];
      x = par[x];
    }
    return x;
  };
  base.edges.forEach((e, k) => {
    if (w[k] > 0) par[find(e.a)] = find(e.b);
  });
  for (let i = 1; i < base.isl.length; i++) if (find(i) !== find(0)) return false;
  return true;
}

/** Cœur de propagation : poids déduits (-1 = inconnu), null si contradiction. */
function haPropCore(p: PontsPuzzle, noDouble: boolean): number[] | null {
  const E = p.edges.length,
    cap0 = noDouble ? 1 : 2;
  const w = new Array(E).fill(-1); // -1 unknown
  const shown = p.shown;
  const inc: number[][] = p.isl.map(() => []); // incidences île → arêtes (précalcul)
  p.edges.forEach((e, k) => {
    inc[e.a].push(k);
    inc[e.b].push(k);
  });
  let guard = 0;
  for (;;) {
    if (++guard > 200) break;
    let ch = false;
    for (let e = 0; e < E; e++) {
      if (w[e] > 0) {
        for (const f of p.cross[e])
          if (w[f] !== 0) {
            if (w[f] > 0) return null;
            w[f] = 0;
            ch = true;
          }
      }
    }
    for (let i = 0; i < p.isl.length; i++) {
      if (!shown[i]) continue;
      const myE = inc[i];
      let fixed = 0;
      const rem: number[] = [];
      for (const k of myE) {
        if (w[k] >= 0) fixed += w[k];
        else rem.push(k);
      }
      const needLeft = p.need[i] - fixed;
      if (needLeft < 0) return null;
      if (needLeft === 0) {
        for (const k of rem)
          if (w[k] !== 0) {
            w[k] = 0;
            ch = true;
          }
        continue;
      }
      let capSum = 0;
      const caps = rem.map((k) => {
        let cp = cap0;
        if (p.cross[k].some((f) => w[f] > 0)) cp = 0;
        capSum += cp;
        return cp;
      });
      if (capSum < needLeft) return null;
      if (capSum === needLeft) {
        rem.forEach((k, ii) => {
          if (w[k] !== caps[ii]) {
            w[k] = caps[ii];
            ch = true;
          }
        });
        continue;
      }
      if (rem.length === 1) {
        if (w[rem[0]] !== needLeft) {
          w[rem[0]] = needLeft;
          ch = true;
        }
        continue;
      }
    }
    if (!ch) break;
  }
  return w;
}

export function haPropagate(p: PontsPuzzle, noDouble: boolean): number[] | false | null {
  const w = haPropCore(p, noDouble);
  if (w === null) return null;
  if (w.some((v) => v === -1)) return false;
  return w.every((v, i) => v === p.solW[i]) ? w : false;
}

function islAt(p: PontsPuzzle, r: number, c: number): number {
  for (let i = 0; i < p.isl.length; i++) if (p.isl[i].r === r && p.isl[i].c === c) return i;
  return -1;
}

export const PONTS: GameEngine<PontsPuzzle> = {
  id: 'ponts',
  name: 'Ponts',
  icon: '🏝️',
  accent: '#80ed99',
  tag: "Relie l'archipel, île par île.",
  how: "Chaque île affiche son <b>nombre de ponts</b>. Trace des ponts <b>horizontaux ou verticaux</b> (1 ou 2 par liaison), <b>sans croisement</b>, jusqu'à connecter tout l'archipel. <b>Touche une île puis sa voisine</b> pour poser/retirer un pont.",
  sizes: [7, 10, 13, 16],
  defis: [
    {
      n: 'Archipel',
      ic: '🏝️',
      d: 'Chaque île affiche son compte de ponts : relie-les toutes, sans se fier aux corridors pièges.',
    },
    {
      n: 'Passerelles',
      ic: '🌉',
      d: 'Ponts doubles interdits : un seul lien entre deux îles, et des corridors à laisser vides.',
    },
    { n: 'Îles muettes', ic: '❓', d: 'Des îles sans chiffre : à toi de deviner leurs besoins.' },
    { n: 'Trois vies', ic: '❤️', d: 'Un croisement ou une surcharge = une vie en moins.' },
    {
      n: 'Grand large',
      ic: '🧭',
      d: "Un océan d'îles de plus en plus dense à réunir en un seul réseau.",
    },
    {
      n: 'Brouillard',
      ic: '🌫️',
      d: 'Îles muettes et ponts simples, en pleine purée de pois.',
    },
  ],
  gen(di, N, diff, rng) {
    const noDouble = di === 1 || di === 5;
    // densité relevée sans ponts doubles : il faut plus d'îles pour autant de liens + leurres
    const islT0 = Math.round(
      N *
        N *
        (di === 4 ? [0.26, 0.3, 0.34] : noDouble ? [0.2, 0.24, 0.28] : [0.18, 0.22, 0.26])[diff],
    );
    const extraP = [0.25, 0.4, 0.5][diff];
    const muet = di === 2 || di === 5;
    const T = N >= 13 ? 128 : 64; // grandes grilles : plus d'essais avant repli
    const RL = T / 4;
    // repli à deux étages : candidat « solide » (équitable, ou muet) au max de
    // leurres sinon candidat au meilleur taux de propagation partielle (N ≥ 13).
    const SOLID = 1_000_000;
    let best: PontsPuzzle | null = null;
    let bestScore = -1;
    const keepBest = (puz: PontsPuzzle, solid: boolean, quality: number): void => {
      let score: number;
      if (solid) score = SOLID + quality;
      else {
        const w = haPropCore(puz, puz.noDouble);
        score =
          w === null ? -1 : (999 * w.reduce((s, v) => s + (v === -1 ? 0 : 1), 0)) / (w.length || 1);
      }
      if (score > bestScore) {
        bestScore = score;
        best = puz;
      }
    };
    for (let t = 0; t < T; t++) {
      // passé la mi-course, un repli équitable ET résistant au jeu aveugle suffit
      if (t >= 2 * RL && bestScore >= SOLID + 100_000) break;
      const round = Math.floor(t / RL); // densité dégressive, puis treillis en dernier recours
      const islT = Math.max(
        5,
        Math.round(islT0 * Math.pow(0.82, round) * (1 - ((t % RL) / RL) * 0.19)),
      );
      const base = haGen(N, islT, noDouble, extraP * Math.pow(0.75, round), round >= 3, rng);
      if (!base) continue;
      const hideQ = muet ? [0.25, 0.35, 0.45][diff] : 0;
      const shown = base.isl.map(() => rng() >= hideQ);
      if (hideQ > 0 && shown.every((s) => s)) shown[ri(rng, shown.length)] = false;
      const puz: PontsPuzzle = {
        g: 'ponts',
        pid: ri(rng, 1e9),
        N,
        di,
        par: base.solW.reduce((s, v) => s + v, 0),
        noDouble,
        isl: base.isl,
        edges: base.edges,
        cross: base.cross,
        solW: base.solW,
        need: base.need,
        shown,
        w: new Array(base.edges.length).fill(0),
        lives: di === 3 ? 3 : null,
        livesMax: 3,
      };
      // au round treillis la porte est abandonnée MAIS on ne retourne plus
      // aveuglément : le meilleur repli (max de leurres) prime en fin de boucle
      const blind = noDouble && blindWins(base, shown);
      const pass =
        round < 3 && !blind && gateOk(base, shown, di, N, diff, noDouble, 1 - 0.3 * round);
      // qualité du repli : échouer au jeu aveugle prime, puis le nb de leurres
      const q = (blind ? 0 : 100_000) + base.solW.filter((v) => v === 0).length;
      if (muet) {
        // muettes : les îles « ? » sont libres, pas d'exigence d'unicité
        if (pass) return puz;
        keepBest(puz, true, q);
        continue;
      }
      // recalé sans espoir d'améliorer le repli : on économise la propagation
      if (!pass && bestScore >= SOLID + q) continue;
      if (haPropagate(puz, noDouble)) {
        if (pass) return puz;
        keepBest(puz, true, q);
      } else if (bestScore < SOLID) keepBest(puz, false, q);
    }
    if (best) return best;
    return {
      g: 'ponts',
      pid: ri(rng, 1e9),
      N,
      di,
      par: 0,
      noDouble,
      isl: [],
      edges: [],
      cross: [],
      solW: [],
      need: [],
      shown: [],
      w: [],
      lives: null,
    };
  },
  tap(p, r, c) {
    if (p.lives === 0) return false;
    const i = islAt(p, r, c);
    if (i < 0) {
      sel = null;
      return 'ui';
    }
    if (sel == null || sel === i) {
      sel = i;
      return 'ui';
    }
    const j = sel;
    let eIdx = -1;
    p.edges.forEach((e, k) => {
      if ((e.a === i && e.b === j) || (e.a === j && e.b === i)) eIdx = k;
    });
    if (eIdx < 0) {
      sel = i;
      return 'ui';
    }
    const cap = p.noDouble ? 1 : 2;
    let nv = (p.w[eIdx] + 1) % (cap + 1);
    if (nv > 0 && p.cross[eIdx].some((f) => p.w[f] > 0)) {
      if (p.lives != null) {
        p.lives--;
        sel = null;
        return true;
      }
      sel = null;
      return 'ui';
    }
    if (nv > 0 && p.lives != null) {
      const ia = p.edges[eIdx].a,
        ib = p.edges[eIdx].b;
      const over = (v: number): boolean => {
        const degA = p.edges.reduce(
          (s, e, k) => s + (e.a === ia || e.b === ia ? (k === eIdx ? v : p.w[k]) : 0),
          0,
        );
        const degB = p.edges.reduce(
          (s, e, k) => s + (e.a === ib || e.b === ib ? (k === eIdx ? v : p.w[k]) : 0),
          0,
        );
        return (p.shown[ia] && degA > p.need[ia]) || (p.shown[ib] && degB > p.need[ib]);
      };
      if (nv === 2 && over(2))
        nv = 0; // transit de retrait 1→2→0 : on saute le double en surcharge, sans pénalité
      else if (over(nv)) {
        p.lives--;
        sel = null;
        return true;
      }
    }
    p.w[eIdx] = nv;
    sel = null;
    return true;
  },
  status(p) {
    if (!p.isl.length) return { ok: false, txt: '⚠️ génération impossible — 🔀 Nouveau' };
    if (p.lives === 0) return { ok: false, txt: '💔 <b>Plus de vies…</b> 🔀 Nouvel archipel' };
    const deg = new Array(p.isl.length).fill(0);
    p.edges.forEach((e, k) => {
      deg[e.a] += p.w[k];
      deg[e.b] += p.w[k];
    });
    let badNum = 0,
      over = false,
      shownN = 0;
    for (let i = 0; i < p.isl.length; i++) {
      if (!p.shown[i]) continue; // île muette : seul le raccordement compte
      shownN++;
      if (deg[i] !== p.need[i]) badNum++;
      if (deg[i] > p.need[i]) over = true;
    }
    const par = [...Array(p.isl.length).keys()];
    const find = (x: number): number => {
      while (par[x] !== x) {
        par[x] = par[par[x]];
        x = par[x];
      }
      return x;
    };
    p.edges.forEach((e, k) => {
      if (p.w[k] > 0) par[find(e.a)] = find(e.b);
    });
    const roots = new Set<number>();
    for (let i = 0; i < p.isl.length; i++) roots.add(find(i));
    const conn = roots.size === 1;
    if (badNum === 0 && conn) return { ok: true, txt: "🎉 <b>L'archipel est uni !</b>" };
    const bits = [`🏝 ${shownN - badNum}/${shownN} îles satisfaites`, `🧭 ${roots.size} groupes`];
    if (over) bits.push('⚠️ île en surcharge');
    if (p.lives != null) bits.push('❤️'.repeat(p.lives) + '🖤'.repeat(p.livesMax! - p.lives));
    return { ok: false, txt: bits.join(' · ') };
  },
  resetSel() {
    sel = null;
  },
  draw(p, ctx, layout) {
    const S = layout.size,
      N = p.N,
      cs = S / N;
    ctx.fillStyle = '#0a1f2e';
    ctx.fillRect(0, 0, S, S);
    ctx.fillStyle = 'rgba(255,255,255,.03)';
    for (let r = 0; r < N; r++)
      for (let c = 0; c < N; c++)
        if ((r * 3 + c * 7) % 5 === 0) ctx.fillRect((c + 0.42) * cs, (r + 0.42) * cs, 2, 2);
    const st = this.status(p);
    p.edges.forEach((e, k) => {
      if (!p.w[k]) return;
      const A = p.isl[e.a],
        B = p.isl[e.b];
      const x1 = (A.c + 0.5) * cs,
        y1 = (A.r + 0.5) * cs,
        x2 = (B.c + 0.5) * cs,
        y2 = (B.r + 0.5) * cs;
      ctx.strokeStyle = st.ok ? '#80ed99' : '#d9b45f';
      ctx.lineWidth = Math.max(2, cs * 0.07);
      ctx.lineCap = 'round';
      if (st.ok) {
        ctx.shadowColor = '#80ed99';
        ctx.shadowBlur = cs * 0.2;
      }
      if (p.w[k] === 1) {
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      } else {
        const off = cs * 0.09,
          dx = e.dir ? off : 0,
          dy = e.dir ? 0 : off;
        ctx.beginPath();
        ctx.moveTo(x1 - dx, y1 - dy);
        ctx.lineTo(x2 - dx, y2 - dy);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x1 + dx, y1 + dy);
        ctx.lineTo(x2 + dx, y2 + dy);
        ctx.stroke();
      }
      ctx.shadowBlur = 0;
    });
    const deg = new Array(p.isl.length).fill(0);
    p.edges.forEach((e, k) => {
      deg[e.a] += p.w[k];
      deg[e.b] += p.w[k];
    });
    p.isl.forEach((il, i) => {
      const x = (il.c + 0.5) * cs,
        y = (il.r + 0.5) * cs,
        rr = cs * 0.36;
      const done = p.shown[i] && deg[i] === p.need[i];
      const overload = p.shown[i] && deg[i] > p.need[i];
      ctx.fillStyle = st.ok ? '#123f2a' : done ? '#12333f' : '#132b3a';
      ctx.beginPath();
      ctx.arc(x, y, rr, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = overload ? '#ff5470' : done || st.ok ? '#80ed99' : '#4a7c96';
      ctx.lineWidth = Math.max(1.5, cs * 0.05);
      if (sel === i) {
        ctx.strokeStyle = '#ffd166';
        ctx.lineWidth = Math.max(2.5, cs * 0.08);
      }
      ctx.beginPath();
      ctx.arc(x, y, rr, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = overload ? '#ff5470' : '#e8f4ea';
      ctx.font = `bold ${cs * 0.4}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(p.shown[i] ? String(p.need[i]) : '?', x, y + 1);
    });
  },
  solveIt(p) {
    p.w = p.solW.slice();
  },
};
